'use client'

import * as React from 'react'
import { GanttChart, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { cn } from 'lib/utils'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProject } from 'lib/access'

const statusConfig = {
  completed: { color: 'bg-success/15 text-success', icon: <CheckCircle className="size-3" /> },
  'in-progress': { color: 'bg-primary/15 text-primary', icon: <Clock className="size-3" /> },
  delayed: { color: 'bg-warning/15 text-warning', icon: <AlertCircle className="size-3" /> },
  'not-started': { color: 'bg-muted text-muted-foreground', icon: <Clock className="size-3" /> },
}

const statusProgress = {
  completed: 100,
  'in-progress': 60,
  delayed: 40,
  'not-started': 0,
}

const formatMonthYear = (value) => {
  if (!value) return '—'
  const parsed = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-KE', { month: 'short', year: 'numeric' })
}

// Simple Gantt bar component
function GanttBar({ activity }) {
  const config = statusConfig[activity.status] || statusConfig['not-started']

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className={cn('text-[10px] gap-1', config.color)}>
            {config.icon}
            {activity.status.replace('-', ' ')}
          </Badge>
          <span className="text-xs font-medium text-foreground truncate">{activity.name}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span>{activity.owner}</span>
          <span>{activity.start} - {activity.end}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 w-32">
        <Progress value={activity.progress} className="h-1.5 flex-1" />
        <span className="text-[10px] font-medium text-foreground w-8 text-right">{activity.progress}%</span>
      </div>
    </div>
  )
}

export default function WorkplansPage() {
  const { user, accessLevel } = useUser()
  const [projects, setProjects] = React.useState([])
  const [workplans, setWorkplans] = React.useState({})
  const [activeProjectId, setActiveProjectId] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadWorkplans() {
      try {
        const [projectSnap, deliverableSnap] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'deliverables')),
        ])
        if (!isMounted) return
        const fetchedProjects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const deliverables = deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const limited = accessLevel === 'limited'
        const currentUserId = getUserId(user)
        const visibleProjects = limited
          ? fetchedProjects.filter((project) => isUserAssociatedWithProject(project, currentUserId))
          : fetchedProjects
        const visibleProjectIds = new Set(visibleProjects.map((project) => project.id))
        const visibleDeliverables = limited
          ? deliverables.filter((item) => visibleProjectIds.has(item.projectId))
          : deliverables

        const grouped = visibleDeliverables.reduce((acc, item) => {
          const projectId = item.projectId || 'unknown'
          const normalizedStatus = String(item.status || 'not_started').replace(/_/g, '-')
          const rawProgress = Number(item.progress)
          const progressValue = Number.isFinite(rawProgress)
            ? rawProgress
            : statusProgress[normalizedStatus] ?? 0
          const ownerName = item.leadPersonName
            || item.responsiblePersonName
            || item.responsiblePersons?.[0]?.name
            || 'Unassigned'
          const entry = {
            id: item.id,
            name: item.name || item.title || 'Untitled deliverable',
            status: normalizedStatus,
            progress: progressValue,
            start: formatMonthYear(item.plannedStart),
            end: formatMonthYear(item.plannedEnd),
            owner: ownerName,
          }
          acc[projectId] = acc[projectId] ? [...acc[projectId], entry] : [entry]
          return acc
        }, {})

        setProjects(visibleProjects)
        setWorkplans(grouped)
        setActiveProjectId((prev) => prev || visibleProjects[0]?.id || '')
      } catch {
        if (!isMounted) return
        setProjects([])
        setWorkplans({})
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadWorkplans()

    return () => {
      isMounted = false
    }
  }, [accessLevel, user?.userId, user?.uid])

  const visibleProjects = projects.length > 0 ? projects : []

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            Workplans & Scheduling
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Work breakdown structures, Gantt charts, and task management
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card className="border border-border">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading workplans...
          </CardContent>
        </Card>
      ) : visibleProjects.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No projects found yet.
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeProjectId} onValueChange={setActiveProjectId}>
          <TabsList>
            {visibleProjects.map((project) => (
              <TabsTrigger key={project.id} value={project.id} className="text-xs">
                {project.code || project.shortCode || project.title || project.id}
              </TabsTrigger>
            ))}
          </TabsList>

          {visibleProjects.map((project) => {
            const activities = workplans[project.id] || []
            const completedActivities = activities.filter((a) => a.status === 'completed').length
            const totalProgress = activities.length > 0
              ? Math.round(activities.reduce((sum, a) => sum + a.progress, 0) / activities.length)
              : 0

            return (
              <TabsContent key={project.id} value={project.id} className="mt-4 flex flex-col gap-4">
              {/* Project Summary */}
              <Card className="border border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold text-foreground">
                        {project.title || 'Untitled project'}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        PI: {project.principalInvestigatorName || project.pi || 'Not set'} | {project.location || project.department || '—'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <GanttChart className="size-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-foreground">
                        {completedActivities}/{activities.length} activities
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <Progress value={totalProgress} className="flex-1 h-2" />
                    <span className="text-sm font-bold text-foreground">{totalProgress}%</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(statusConfig).map(([status, config]) => {
                      const count = activities.filter((a) => a.status === status).length
                      if (count === 0) return null
                      return (
                        <Badge key={status} className={cn('text-[10px] gap-1', config.color)}>
                          {config.icon}
                          {status.replace('-', ' ')}
                        </Badge>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Activity List (Gantt-like) */}
              <Card className="border border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-foreground">
                    Work Breakdown Structure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    {activities.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No deliverables added for this project yet.
                      </p>
                    ) : (
                      activities.map((activity) => (
                        <GanttBar key={activity.id} activity={activity} />
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            )
          })}
        </Tabs>
      )}
    </div>
  )
}
